// useRequestPersistence
// ---------------------
// The request create / save / submit handlers, extracted out of the legacy
// portal.jsx App component so they can be unit-tested in isolation. portal.jsx
// has no test harness and building one for the whole App component is
// disproportionate (it owns auth, session, routing and every child). The durable
// fix is to lift the persistence logic into a tested unit and leave the App as
// thin wiring -- the same direction as the earlier component/hook decompositions.
//
// This is a state-bag factory, the same pattern as useUserAdminHandlers: it
// receives the App's relevant setters + refs + the supabase client, and returns
// the four cohesive persistence handlers. The bodies are a VERBATIM move from
// portal.jsx (no behaviour change): they route the Draft->Submitted transition
// through nextStatusOnSubmit (the guarded lifecycle reducer) and map app<->db via
// reqToDb / dbToReq, exactly as before.
//
// TypeScript source -- useRequestPersistence.js is a thin re-export stub.

import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import {
  getRequestViewRowById,
  insertRequest,
  saveRequestChildren,
  updateRequestById,
  updateRequestByIdReturning,
} from "../data/requests";
import { nextStatusOnSubmit } from "../services/requestLifecycle";
import { requestStamp } from "../services/requestStamp";
import { logAudit } from "../utils/auditLog";
import { REQUEST_STATUSES } from "../utils/enums";
import { dbToReq, reqToDb } from "../utils/helpers";
import { logger } from "../utils/logger";
import { contactNameForUser } from "../utils/sessionUser";

// Derive the helper shapes without importing the (unexported) interfaces.
type AppRequest = ReturnType<typeof dbToReq>;
type DbViewRow = Parameters<typeof dbToReq>[0];
type ReqDbInput = Parameters<typeof reqToDb>[0];
type SaveUser = NonNullable<Parameters<typeof reqToDb>[1]>;
type SubmitStatusArg = Parameters<typeof nextStatusOnSubmit>[0];

// The loose user object the App holds. It satisfies reqToDb's user param and
// also carries the display fields saveDraft reads when building a new draft row.
type AppUser = SaveUser & {
  shelterName?: string;
  shelterCode?: string;
  state?: string;
  shelterAddress?: string;
  // Needed by requestStamp to record WHICH SIDE (Zephyr or the service) wrote.
  role?: string | null;
};

// Input from the wizard / forms: mapped request fields plus the transient flags
// the wizard sets to coordinate with the parent (already-saved, exit-after-save).
interface DraftData {
  [key: string]: unknown;
  status?: string | null;
  refNo?: string | null;
  children?: unknown;
  _alreadySaved?: boolean;
  _dbRow?: unknown;
  _exitAfterSave?: boolean;
}

export interface RequestPersistenceDeps {
  user: AppUser;
  editingDraft: AppRequest | null;
  setRequests: Dispatch<SetStateAction<AppRequest[]>>;
  setNewReq: Dispatch<SetStateAction<boolean>>;
  setSel: Dispatch<SetStateAction<AppRequest | null>>;
  setEditingDraft: Dispatch<SetStateAction<AppRequest | null>>;
  pendingDraftIdRef: MutableRefObject<number | null>;
  pendingDraftRefNoRef: MutableRefObject<string | null>;
}

export function useRequestPersistence(deps: RequestPersistenceDeps) {
  const {
    user,
    editingDraft,
    setRequests,
    setNewReq,
    setSel,
    setEditingDraft,
    pendingDraftIdRef,
    pendingDraftRefNoRef,
  } = deps;

  // save_request_children RPC. Called after any insert/update of a request.
  async function saveChildrenRPC(requestId: string | number | null | undefined, children: unknown) {
    if (!requestId) return;
    await saveRequestChildren(requestId, children);
  }

  async function updateRequest(updatedReq: AppRequest, skipDbWrite?: boolean) {
    try {
      const withMeta = Object.assign({}, updatedReq, {
        updatedBy: user.shelterName,
        updatedAt: new Date().toISOString(),
      });
      if (!skipDbWrite) {
        // Staff/admin editors that don't do their own DB write.
        // Preserve the request's EXISTING delivery link. A staff/admin editor has no
        // shelter deliveryAddresses, so reqToDb's resolveDeliveryForSave can't match a
        // registered address and would null delivery_address_id, flattening it into a
        // one-off delivery_address_override text (which then can't split on the Sales
        // Order). This path only edits items — the delivery address is changed solely
        // via EditAddressModal, which writes it on its own path — so carrying the
        // loaded link through unchanged is always correct here.
        const row = Object.assign({}, reqToDb(updatedReq as unknown as ReqDbInput, user), {
          ...requestStamp(user),
          delivery_address_id: updatedReq.deliveryAddressId ?? null,
          delivery_address_override: updatedReq.deliveryAddressOverride ?? null,
        });
        const { error } = await updateRequestById(updatedReq.id, row);
        if (error) {
          logger.warn("updateRequest DB write failed:", error.message);
          return;
        }
        // Stage 1: persist children via RPC after the request row is saved
        await saveChildrenRPC(updatedReq.id, updatedReq.children);
      }
      setRequests(function (prev) {
        return prev.map(function (r) {
          return r.id === withMeta.id ? withMeta : r;
        });
      });
      setSel(withMeta);
    } catch (e) {
      logger.warn("updateRequest error:", e);
    }
  }

  async function saveDraft(draftData: DraftData) {
    // If wizard already saved to DB (submission path), just update the list
    if (draftData._alreadySaved) {
      const saved: AppRequest = draftData._dbRow
        ? dbToReq(draftData._dbRow as DbViewRow)
        : (draftData as unknown as AppRequest);
      setRequests((prev) => {
        var exists = prev.some(function (r) {
          return r.id === saved.id;
        });
        return exists
          ? prev.map(function (r) {
              return r.id === saved.id ? saved : r;
            })
          : [saved, ...prev];
      });
      setNewReq(false);
      pendingDraftIdRef.current = null;
      pendingDraftRefNoRef.current = null;
      return;
    }
    var isSubmitting = draftData.status === REQUEST_STATUSES.SUBMITTED;
    var existingDraftId = pendingDraftIdRef.current;
    // When updating an existing draft, preserve its ref_no -- sending null would violate NOT NULL.
    const refNo = isSubmitting
      ? draftData.refNo
      : existingDraftId
        ? pendingDraftRefNoRef.current
        : `DRAFT-${Date.now()}`;
    const row = reqToDb(
      Object.assign(
        {
          refNo,
          shelterName: user.shelterName,
          shelterCode: user.shelterCode || "",
          state: user.state,
          shelterAddress: user.shelterAddress,
          // Record the caseworker's own name on the draft (the wizard's draft-save
          // objects omit contactName — only the submit builder set it — so drafts used
          // to save contact_name blank). A fallback: draftData's value (the submit
          // path) still wins via the Object.assign below.
          contactName: contactNameForUser(user),
          // Route Draft->Submitted through the guarded reducer (mirror of db-8).
          status: isSubmitting ? nextStatusOnSubmit(REQUEST_STATUSES.DRAFT) : "Draft",
          date: new Date().toISOString().slice(0, 10),
        },
        draftData
      ) as unknown as ReqDbInput,
      user
    );
    const dbOp = existingDraftId
      ? updateRequestByIdReturning(existingDraftId, row)
      : insertRequest(row);
    const { data, error } = await dbOp;
    if (!error && data) {
      // Stage 1: persist children via RPC, then re-hydrate from view so dbToReq sees them
      await saveChildrenRPC(data.id, draftData.children);
      var hydrated: DbViewRow = data as unknown as DbViewRow;
      var view = await getRequestViewRowById(data.id);
      if (view) hydrated = view;
      const saved = dbToReq(hydrated);
      if (!existingDraftId) {
        // First save -- record the draft ID + ref_no, add to list
        pendingDraftIdRef.current = data.id;
        pendingDraftRefNoRef.current = data.ref_no;
        // Replace-if-present, else prepend. The Realtime `requests` listener
        // (portal.jsx) can reload the whole list between our insert and this
        // line, in which case the row is already here — a plain prepend showed
        // the new draft twice until the next reload (seen on staging, 23 Oct 2026).
        setRequests((prev) =>
          prev.some((r) => r.id === saved.id)
            ? prev.map((r) => (r.id === saved.id ? saved : r))
            : [saved, ...prev]
        );
        // Audit the draft's creation so a draft's lifecycle (created -> deleted or
        // submitted) is traceable -- e.g. if a shelter later claims a draft was
        // submitted when it was actually deleted. Only the first save is logged
        // (not every auto-save) to keep the trail readable.
        if (!isSubmitting)
          logAudit("draft_saved", {
            request_id: data.id,
            ref: data.ref_no,
            shelter: user.shelterCode,
            by: user.shelterName,
          });
      } else {
        // Update existing entry in list
        setRequests((prev) =>
          prev.map(function (r) {
            return r.id === saved.id ? saved : r;
          })
        );
      }
      if (isSubmitting) {
        setNewReq(false);
        pendingDraftIdRef.current = null;
        pendingDraftRefNoRef.current = null;
      } else if (draftData._exitAfterSave) {
        setNewReq(false);
        setSel(saved);
        pendingDraftIdRef.current = null;
        pendingDraftRefNoRef.current = null;
      }
      // Otherwise (auto-save on step transition) -- stay in wizard
    }
  }

  async function updateDraft(draftData: DraftData) {
    // Called both for Save Draft and for Submit (status may be "Submitted").
    // Guard: updateDraft only runs while a draft is being edited.
    if (!editingDraft) return;
    const merged = Object.assign({}, editingDraft, draftData);
    // Fill contact_name for a draft saved without it (the wizard's draft-save objects
    // omit contactName; only the submit builder set it). Fallback only — never override
    // an explicit value. (docs/consolidate-login-buildsessionuser-plan.md)
    if (!merged.contactName) merged.contactName = contactNameForUser(user);
    if (draftData.status === REQUEST_STATUSES.SUBMITTED) {
      // Route the Draft->Submitted transition through the guarded reducer (mirror
      // of db-8). SUBMIT only advances a Draft; it no-ops on any other status.
      (merged as DraftData).status = nextStatusOnSubmit(editingDraft.status as SubmitStatusArg);
    }
    var updated: AppRequest;
    if (draftData._alreadySaved) {
      // The wizard's Submit already persisted the request AND its children in a
      // single transaction via upsert_request_atomic. Reuse the returned
      // requests_view row instead of issuing a second, redundant update +
      // save_request_children. (The RPC row is the superset of reqToDb's columns,
      // so nothing is lost by skipping the extra write.)
      updated = draftData._dbRow
        ? dbToReq(draftData._dbRow as DbViewRow)
        : (merged as unknown as AppRequest);
    } else {
      const row = reqToDb(merged as unknown as ReqDbInput, user);
      const { data, error } = await updateRequestByIdReturning(editingDraft.id, row);
      if (error || !data) return;
      // Stage 1: persist children via RPC, then re-hydrate from view
      await saveChildrenRPC(data.id, (merged as DraftData).children);
      var hydrated: DbViewRow = data as unknown as DbViewRow;
      var view = await getRequestViewRowById(data.id);
      if (view) hydrated = view;
      updated = dbToReq(hydrated);
    }
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    if (merged.status === REQUEST_STATUSES.SUBMITTED) {
      setEditingDraft(null);
      setSel(null);
    } else if (draftData._exitAfterSave) {
      // Deliberate Save Draft & Exit -- leave wizard and show draft
      setEditingDraft(null);
      setSel(updated);
    }
    // Otherwise (auto-save on step transition) -- stay in wizard, do nothing
  }

  return { saveChildrenRPC, saveDraft, updateDraft, updateRequest };
}
